"use client";

/**
 * HeroUniversePicker — глобальный «селектор вселенной» для главной (Hub).
 *
 * Идея: универсум (US 100 / S&P 500 / General Watchlist / …) — это не «ещё одна
 * секция дашборда», а **контекст всей страницы**. Поэтому селектор
 * встроен прямо в hero под слоганом «Models at work.»:
 *
 *     Models at work
 *     on  🇺🇸 S&P 500 ▾   ·  500 stocks
 *
 * Клик по триггеру открывает поповер со всем доступным списком:
 *   • Уже добавленные вселенные (★ General Watchlist, US 100, S&P 500, Europe 500
 *     и любые extras), активный отмечен галочкой и тонкой violet-подсветкой.
 *   • Поиск по всем спискам из БД (`list_display_names`).
 *   • Premium-списки помечены 🔒 для бесплатных пользователей.
 *
 * Принимает ровно тот же интерфейс пропсов, что и старый
 * `UniverseSelector` — это сделано осознанно, чтобы упростить миграцию:
 * страница просто меняет один блок на другой и продолжает прокидывать
 * те же handlers (`onSelect`, `onAddList`, `onRemoveExtra`, …).
 */

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { ChevronRight, Crown, Lock, Plus, Search, Star, X, BarChart3, GitMerge, type LucideIcon } from "lucide-react";
import USFlag from "country-flag-icons/react/3x2/US";
import EUFlag from "country-flag-icons/react/3x2/EU";
import { getStrategyIconForCode } from "@/lib/strategy-icons";
import {
  FONT_BASTION,
  PICKER_ACCENT_TEXT,
  pickerOptionRowHoverBg,
  pickerOptionRowSelectionStyle,
} from "@/lib/bastion-button";
import {
  BASTION_GOLD,
  BASTION_GOLD_SOFT,
  BASTION_GOLD_SOFT_HOVER,
} from "@/lib/bastion-accent";
import {
  popoverEnterStyle,
  useHoverPopoverOpen,
  usePopoverEnterAnimation,
} from "@/app/components/use-hover-popover-open";
import { HeroPickerPopoverBackdrop } from "@/app/components/HeroPickerPopoverBackdrop";
import { useHeroPickerPopoverPosition } from "@/app/components/use-hero-picker-popover-position";
import { bindPopoverWheelTrap } from "@/lib/bind-popover-wheel-trap";
import BastionTooltip from "@/lib/components/BastionTooltip";
import { MomentumBars } from "@/lib/components/StockInlineMini";
import {
  heroPickerTriggerHoverHandlers,
  heroPickerTriggerOpenStyle,
  heroPickerTriggerChevronColor,
} from "@/app/components/hero-picker-trigger-hover";
import {
  HEATMAP_TOOLBAR_CHIP_FONT_SIZE,
  heatmapToolbarTriggerDims,
} from "@/app/heatmap/_components/heatmap-secondary-controls";
import {
  BENCHMARK_PORTFOLIO_COLLECTION_ID,
  isBenchmarkCollectionId,
} from "@/app/heatmap/_lib/benchmark-scatter-data";
import { canonicalPickerListType } from "@/lib/list-taxonomy";

const STRATEGY_COLORS: Record<string, string> = {
  PureGrowth: "#4ade80",
  PureQuality: "#a78bfa",
  PureValue: "#facc15",
  PureMomentum: "#f97316",
  PureAntifragility: "#38bdf8",
  GEAR5FScore: "#f472b6",
  QualityGrowth: "#fbbf24",
  GrowthMomentum: "#fb923c",
  ValueMomentum: "#34d399",
};

const US_LISTS = new Set(["sp500", "usa100", "usmidcaps", "usdivaristocrats", "usdivkings", "usa1000", "nasdaq100"]);
const EUROPE_LISTS = new Set(["europe500"]);

type RegionId = "us" | "europe" | "global";

type CombineFilterType =
  | "custom"
  | "country"
  | "sector"
  | "industrygroup"
  | "industry"
  | "portfolio"
  | "watchlist";
type CombineOperator = "AND" | "OR" | "NOT";

/** Config that turns the picker into an in-popover "combine lists" surface. */
export type HeroUniverseCombineConfig = {
  parseLogicSource: (
    rawId: string,
    rawLabel: string,
  ) => { type: CombineFilterType; value: string; label: string };
  addFilter: (
    type: CombineFilterType,
    value: string,
    label: string,
    operator: CombineOperator,
  ) => void;
  authUser: unknown;
  onSignUp: () => void;
  /** Non-premium user picked a list to combine — open the paywall instead of adding it. */
  onShowPro?: () => void;
};

const COMBINE_OP_BG: Record<CombineOperator, string> = {
  AND: "rgba(74, 124, 89, 0.9)",
  OR: "rgba(124, 106, 74, 0.9)",
  NOT: "rgba(124, 74, 74, 0.9)",
};
const COMBINE_OP_THEME: Record<
  CombineOperator,
  {
    toggleBorder: string;
    toggleBorderHover: string;
    toggleBg: string;
    toggleBgHover: string;
    toggleText: string;
    toggleTextHover: string;
    toggleBgPreview: string;
    toggleGlowPreview: string;
    toggleGlowHover: string;
    popoverBorder: string;
    popoverShadow: string;
    bannerBg: string;
    bannerBorder: string;
    labelColor: string;
  }
> = {
  AND: {
    toggleBorder: "rgba(74,124,89,0.75)",
    toggleBorderHover: "rgba(74,124,89,0.95)",
    toggleBg: "rgba(74,124,89,0.38)",
    toggleBgHover: "rgba(74,124,89,0.52)",
    toggleText: "#8fd3a6",
    toggleTextHover: "#b8f0cc",
    toggleBgPreview: "rgba(74,124,89,0.24)",
    toggleGlowPreview: "0 0 10px rgba(74,124,89,0.22)",
    toggleGlowHover: "0 0 12px rgba(74,124,89,0.35)",
    popoverBorder: "rgba(74,124,89,0.6)",
    popoverShadow:
      "0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(74,124,89,0.25), 0 0 26px rgba(74,124,89,0.2)",
    bannerBg: "rgba(74,124,89,0.12)",
    bannerBorder: "rgba(74,124,89,0.35)",
    labelColor: "rgba(143,211,166,0.85)",
  },
  OR: {
    toggleBorder: "rgba(124,106,74,0.75)",
    toggleBorderHover: "rgba(124,106,74,0.95)",
    toggleBg: "rgba(124,106,74,0.38)",
    toggleBgHover: "rgba(124,106,74,0.52)",
    toggleText: "#d4b896",
    toggleTextHover: "#e8cdb0",
    toggleBgPreview: "rgba(124,106,74,0.24)",
    toggleGlowPreview: "0 0 10px rgba(124,106,74,0.22)",
    toggleGlowHover: "0 0 12px rgba(124,106,74,0.35)",
    popoverBorder: "rgba(124,106,74,0.6)",
    popoverShadow:
      "0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(124,106,74,0.25), 0 0 26px rgba(124,106,74,0.2)",
    bannerBg: "rgba(124,106,74,0.12)",
    bannerBorder: "rgba(124,106,74,0.35)",
    labelColor: "rgba(212,184,150,0.85)",
  },
  NOT: {
    toggleBorder: "rgba(124,74,74,0.75)",
    toggleBorderHover: "rgba(124,74,74,0.95)",
    toggleBg: "rgba(124,74,74,0.38)",
    toggleBgHover: "rgba(124,74,74,0.52)",
    toggleText: "#e8a0a0",
    toggleTextHover: "#f0bcbc",
    toggleBgPreview: "rgba(124,74,74,0.24)",
    toggleGlowPreview: "0 0 10px rgba(124,74,74,0.22)",
    toggleGlowHover: "0 0 12px rgba(124,74,74,0.35)",
    popoverBorder: "rgba(124,74,74,0.6)",
    popoverShadow:
      "0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(124,74,74,0.25), 0 0 26px rgba(124,74,74,0.2)",
    bannerBg: "rgba(124,74,74,0.12)",
    bannerBorder: "rgba(124,74,74,0.35)",
    labelColor: "rgba(232,160,160,0.85)",
  },
};
const COMBINE_OP_HINT: Record<CombineOperator, string> = {
  AND: "stocks in both lists",
  OR: "stocks in either list",
  NOT: "exclude these stocks",
};

/** AND / OR / NOT segmented control shown inside the popover while combining. */
function CombineOperatorTabs({
  op,
  onChange,
}: {
  op: CombineOperator;
  onChange: (op: CombineOperator) => void;
}) {
  return (
    <div
      style={{
        display: "inline-flex",
        gap: 3,
        background: "rgba(0,0,0,0.28)",
        borderRadius: 8,
        padding: 3,
        flexShrink: 0,
      }}
    >
      {(["AND", "OR", "NOT"] as CombineOperator[]).map((o) => {
        const active = o === op;
        return (
          <button
            key={o}
            type="button"
            onClick={() => onChange(o)}
            style={{
              padding: "4px 11px",
              borderRadius: 6,
              border: "none",
              background: active ? COMBINE_OP_BG[o] : "transparent",
              color: active ? "#fff" : "rgba(255,255,255,0.5)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.04em",
              cursor: "pointer",
              transition: "all 0.15s",
              fontFamily: "SF Mono, Menlo, Monaco, Consolas, monospace",
            }}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

function regionOfList(listId: string | undefined, name?: string): RegionId {
  if (listId) {
    const l = listId.toLowerCase();
    if (US_LISTS.has(l)) return "us";
    if (EUROPE_LISTS.has(l)) return "europe";
    if (l.includes("europe")) return "europe";
    if (l.startsWith("us") || l.includes("sp500") || l.startsWith("nasdaq")) return "us";
  }
  if (name) {
    const n = name.toLowerCase();
    if (n.startsWith("us ") || n.includes(" us ") || n.startsWith("nasdaq") || n.startsWith("s&p")) return "us";
    if (n.startsWith("europe") || n.includes("europe")) return "europe";
    if (n.startsWith("global") || n.includes(" global ")) return "global";
  }
  return "global";
}

const EMOJI_FONT = '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif';
let _isWin: boolean | null = null;
function isWin(): boolean {
  if (_isWin !== null) return _isWin;
  if (typeof navigator === "undefined") return false;
  _isWin = /Win/i.test(navigator.platform || "") || /Windows/i.test(navigator.userAgent || "");
  return _isWin;
}

function MiniRegionFlag({ region }: { region: RegionId }) {
  const h = 10;
  const w = 15;
  const wrap: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: w,
    height: h,
    borderRadius: 1.5,
    overflow: "hidden",
    flexShrink: 0,
    boxShadow: "0 0 0 0.5px rgba(255,255,255,0.12)",
  };
  const emojiStyle: React.CSSProperties = {
    fontSize: 9,
    lineHeight: 1,
    fontFamily: EMOJI_FONT,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
  if (region === "us") {
    if (isWin()) return <span style={wrap}><USFlag style={{ width: "100%", height: "100%", display: "block" }} /></span>;
    return <span style={emojiStyle}>{"\u{1F1FA}\u{1F1F8}"}</span>;
  }
  if (region === "europe") {
    if (isWin()) return <span style={wrap}><EUFlag style={{ width: "100%", height: "100%", display: "block" }} /></span>;
    return <span style={emojiStyle}>{"\u{1F1EA}\u{1F1FA}"}</span>;
  }
  if (isWin()) {
    return (
      <span style={wrap}>
        <svg viewBox="0 0 18 12" width={w} height={h} style={{ display: "block" }}>
          <rect width="18" height="12" fill="#4b92db" />
          <g transform="translate(9,6)" fill="none" stroke="#fff" strokeWidth="0.7">
            <ellipse rx="3.8" ry="3.8" />
            <ellipse rx="2" ry="3.8" />
            <line x1="-3.8" y1="0" x2="3.8" y2="0" />
            <line x1="0" y1="-3.8" x2="0" y2="3.8" />
            <ellipse rx="3.2" ry="1.5" transform="rotate(90)" />
          </g>
        </svg>
      </span>
    );
  }
  return <span style={emojiStyle}>{"\uD83C\uDF0D"}</span>;
}

type Universe = {
  id: string;
  label: string;
  emoji: string;
  count: number;
  locked: boolean;
  tickers: string[];
  type?: string;
};

type PickerCategory = "quick_picks" | "all" | "recent" | "trending" | "custom" | "countries" | "sectors" | "portfolios" | "watchlists";

type PickerOption = {
  id: string;
  label: string;
  emoji: string;
  premium: boolean;
  type: string;
  count?: number;
  momentum?: number;
  strategyCode?: string;
  universe?: Universe;
  listId?: string;
  quickAccessItemId?: string;
};

interface HeroUniversePickerProps {
  universes: Universe[];
  extraUniverses: Universe[];
  selectedId: string | undefined;
  onSelect: (u: Universe) => void;
  onRemoveExtra: (id: string) => void;
  allLists: { id: string; label: string; emoji: string; premium: boolean; type?: string; count?: number; momentum?: number; strategyCode?: string; listId?: string; quickAccessItemId?: string }[];
  onAddList: (id: string) => void;
  isPaid: boolean;
  user: any;
  onSignUp: () => void;
  /** Free user picked a premium portfolio — open subscription modal. */
  onShowPro?: () => void;
  quickAccessListItemIdsByPickerId?: Record<string, string>;
  canManageQuickAccess?: boolean;
  onAddQuickAccessList?: (pickerId: string) => void;
  onRemoveQuickAccessList?: (itemId: string) => void;
  /** default = large Hub hero, medium = mid-size pill, listChip = compact chip, toolbar = heatmap toolbar row */
  triggerVariant?: "default" | "medium" | "listChip" | "toolbar";
  /** Fixed trigger width (px). Label truncates with ellipsis; full name in tooltip. */
  triggerFixedWidth?: number;
  /** Muted label shown before the value (e.g. "List"), like heatmap Footer/Key Stats chips. */
  triggerLabelPrefix?: string;
  /** Muted stock count after the list name (e.g. 497); omit to hide. */
  triggerCount?: number | null;
  /** Extra control after trigger (shown only while the popover is open). */
  afterTrigger?: ReactNode;
  /** Enables in-popover "combine lists" mode. A "+ Combine" control at the top of
   *  the list popover toggles AND/OR/NOT; picking a list adds it as a filter. */
  combine?: HeroUniverseCombineConfig;
  /** Replace default trigger with a custom render function */
  renderTrigger?: (props: {
    onClick: () => void;
    open: boolean;
    ref: React.RefObject<HTMLElement | null>;
    /** Extra controls after a custom trigger (only while the popover is open). */
    afterTrigger?: ReactNode;
    /** Desktop hover-open — attach to the custom trigger element. */
    onTriggerEnter?: () => void;
    onTriggerLeave?: () => void;
  }) => ReactNode;
  /** Content rendered above the search bar inside the popover (e.g. operator tabs) */
  popoverPrefix?: ReactNode;
  /** Content at the very top of the popover (e.g. formula favorites). */
  popoverHeader?: ReactNode;
  /** Extra control in the combine strip — after operator hint or "+ Combine". */
  combineAccessory?: ReactNode;
  /** When true, open the popover with the AND/OR/NOT combine banner already expanded. */
  combineFormulaActive?: boolean;
  /** Keep popover open after selecting an item (for multi-select scenarios) */
  keepOpenOnSelect?: boolean;
  /** Ordered list ids for the first-tab "Quick Picks" section (heatmap hub shortcuts). */
  quickPickIds?: string[];
  /** Block popover and call `onRequireAuth` on trigger click (anonymous heatmap). */
  requireAuth?: boolean;
  onRequireAuth?: () => void;
  /** Desktop: open on trigger hover with smooth enter animation; touch keeps click toggle. */
  openOnHover?: boolean;
  /** Frozen report sheet — chip look, no chevron, popover disabled. */
  readOnlyTrigger?: boolean;
  /** Intercept trigger click: return true to prevent the popover from opening
   *  (the caller handles the click, e.g. toggling a separate favorites row). */
  interceptTriggerClick?: () => boolean;
  /** When set, drives chevron rotation instead of popover `open` (for external expanders). */
  triggerChevronOpen?: boolean;
  /** Increment to force-close the popover (e.g. Views sidebar opened over it). */
  dismissSignal?: number;
  /** Notified when the list popover opens or closes. */
  onOpenChange?: (open: boolean) => void;
}

export default function HeroUniversePicker({
  universes,
  extraUniverses,
  selectedId,
  onSelect,
  onRemoveExtra,
  allLists,
  onAddList,
  isPaid,
  user,
  onSignUp,
  onShowPro,
  triggerVariant = "default",
  triggerFixedWidth,
  triggerLabelPrefix,
  triggerCount,
  afterTrigger,
  combine,
  renderTrigger,
  popoverPrefix,
  popoverHeader,
  combineAccessory,
  combineFormulaActive = false,
  keepOpenOnSelect = false,
  quickPickIds,
  requireAuth = false,
  onRequireAuth,
  openOnHover = false,
  readOnlyTrigger = false,
  interceptTriggerClick,
  triggerChevronOpen,
  dismissSignal,
  onOpenChange,
  quickAccessListItemIdsByPickerId = {},
  canManageQuickAccess = false,
  onAddQuickAccessList,
  onRemoveQuickAccessList,
}: HeroUniversePickerProps) {
  const {
    open,
    setOpen,
    hoverCapable,
    handleTriggerEnter,
    handleTriggerLeave,
    handlePopoverEnter,
    handlePopoverLeave,
    handleToggleClick: handleHoverToggleClick,
  } = useHoverPopoverOpen(openOnHover && !readOnlyTrigger);
  const popoverEntered = usePopoverEnterAnimation(open && openOnHover && hoverCapable);
  const [combineMode, setCombineMode] = useState(false);
  const [combineOp, setCombineOp] = useState<CombineOperator>("AND");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState<PickerCategory>("all");
  const [expandedMoreGroupKeys, setExpandedMoreGroupKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const { mounted, layout: popoverLayout } = useHeroPickerPopoverPosition(open, triggerRef, popoverRef, false, true);

  const RECENT_KEY = "hub_recent_lists";
  const MAX_RECENT = 12;
  const [recentIds, setRecentIds] = useState<string[]>(() => {
    if (typeof window === "undefined") return [];
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; }
  });
  const trackRecent = useCallback((id: string) => {
    setRecentIds((prev) => {
      const next = [id, ...prev.filter((x) => x !== id)].slice(0, MAX_RECENT);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const allActive = useMemo(
    () => [...universes, ...extraUniverses],
    [universes, extraUniverses],
  );
  const selected = useMemo(
    () => allActive.find((u) => u.id === selectedId) || null,
    [allActive, selectedId],
  );

  useEffect(() => {
    if (selectedId) trackRecent(selectedId);
  }, [selectedId, trackRecent]);

  useEffect(() => {
    if (!open) setExpandedMoreGroupKeys(new Set());
  }, [open]);

  // Reset combine when closed; reopen with banner expanded when a formula is already active.
  // Use combineEnabled (boolean) — the combine config object is often recreated inline by parents.
  const combineEnabled = Boolean(combine);
  useEffect(() => {
    if (!open) {
      setCombineMode((prev) => (prev ? false : prev));
      return;
    }
    if (combineFormulaActive && combineEnabled) {
      setCombineMode((prev) => (prev ? prev : true));
    }
  }, [open, combineFormulaActive, combineEnabled]);

  useEffect(() => {
    if (readOnlyTrigger) setOpen(false);
  }, [readOnlyTrigger]);

  useEffect(() => {
    if (!dismissSignal) return;
    setOpen(false);
  }, [dismissSignal, setOpen]);

  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  const handleCombineToggle = useCallback(() => {
    if (!combine) return;
    if (!combine.authUser) {
      combine.onSignUp();
      return;
    }
    setCombineMode((v) => !v);
  }, [combine]);

  // Закрытие по клику вне поповера / Esc
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(t) &&
        triggerRef.current &&
        !triggerRef.current.contains(t)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Prevent scroll propagation from popover to page
  useEffect(() => {
    if (!open || !popoverLayout) return;
    const el = popoverRef.current;
    if (!el) return;
    return bindPopoverWheelTrap(el);
  }, [open, popoverLayout]);

  const options = useMemo<PickerOption[]>(() => {
    const activeById = new Map(allActive.map((u) => [u.id, u]));
    const rows = allLists.map((l) => ({
      id: l.id,
      label: l.label,
      emoji: l.emoji,
      premium: l.premium,
      type: resolvePickerListType(l.id, l.type),
      universe: activeById.get(l.id),
      count: activeById.get(l.id)?.count ?? l.count,
      momentum: l.momentum,
      strategyCode: l.strategyCode,
      listId: l.listId,
      quickAccessItemId: l.quickAccessItemId ?? quickAccessListItemIdsByPickerId[l.id],
    }));

    for (const u of allActive) {
      if (!rows.some((r) => r.id === u.id)) {
        const listEntry = allLists.find((l) => l.id === u.id);
        rows.unshift({
          id: u.id,
          label: u.label,
          emoji: u.emoji,
          premium: u.locked,
          type: resolvePickerListType(
            u.id,
            u.type || (u.id === "shelf" ? "watchlist" : "list"),
          ),
          universe: u,
          count: u.count,
          momentum: listEntry?.momentum,
        });
      }
    }

    return rows.filter(
      (r) => r.label !== "N/A" && r.id !== "NA" && !isPortfolioBenchmarkOption(r),
    );
  }, [allLists, allActive, quickAccessListItemIdsByPickerId]);

  const trendingAll = useMemo(() => {
    return dedupeByLabel(
      [...options]
        .filter((o) => o.momentum != null && (o.count ?? 0) >= 7 && normalizeListType(o.type) !== "portfolio")
        .sort((a, b) => (b.momentum ?? 0) - (a.momentum ?? 0))
    ).slice(0, 5);
  }, [options]);

  const trendingByCategory = useMemo(() => {
    const result: Record<string, PickerOption[]> = {};
    for (const cat of CATEGORY_ORDER) {
      const catOptions = dedupeByLabel(
        options
          .filter((o) => categoryForType(o.type) === cat && o.momentum != null && (o.count ?? 0) >= 7)
          .sort((a, b) => (b.momentum ?? 0) - (a.momentum ?? 0))
      ).slice(0, 5);
      if (catOptions.length > 0) result[cat] = catOptions;
    }
    return result;
  }, [options]);

  const favoriteOptions = useMemo(() => {
    if (!quickPickIds?.length) return [];
    const order = new Map(quickPickIds.map((id, i) => [id, i]));
    return dedupeByLabel(
      options
        .filter((o) => order.has(o.id))
        .sort((a, b) => order.get(a.id)! - order.get(b.id)!),
    );
  }, [options, quickPickIds]);

  const categoryBaseOptions = useMemo(() => {
    if (activeCategory === "recent") {
      const idxMap = new Map(recentIds.map((id, i) => [id, i]));
      return dedupeByLabel(
        options
          .filter((o) => idxMap.has(o.id))
          .sort((a, b) => idxMap.get(a.id)! - idxMap.get(b.id)!),
      );
    }
    if (activeCategory === "all") {
      return options;
    }
    return options.filter((o) => categoryForType(o.type) === activeCategory);
  }, [options, activeCategory, recentIds]);

  const tabSearchMatches = useMemo(() => {
    const s = searchTerm.toLowerCase().trim();
    return categoryBaseOptions.filter(
      (o) => s === "" || o.label.toLowerCase().includes(s) || o.id.toLowerCase().includes(s),
    );
  }, [categoryBaseOptions, searchTerm]);

  const globalSearchMatches = useMemo(() => {
    const s = searchTerm.toLowerCase().trim();
    if (!s) return [];
    const tabIds = new Set(tabSearchMatches.map((o) => o.id));
    return options.filter(
      (o) =>
        !tabIds.has(o.id) &&
        (o.label.toLowerCase().includes(s) || o.id.toLowerCase().includes(s)),
    );
  }, [options, searchTerm, tabSearchMatches]);

  type DisplayGroup = {
    key: string;
    label: string;
    items: PickerOption[];
    isTrending?: boolean;
    moreItems?: PickerOption[];
  };

  const displayGroups = useMemo((): DisplayGroup[] => {
    const s = searchTerm.trim();

    if (s !== "") {
      const groups: DisplayGroup[] = [];
      if (tabSearchMatches.length > 0) {
        groups.push({
          key: "search-tab",
          label: searchSectionLabel(activeCategory),
          items: tabSearchMatches,
        });
      }
      if (globalSearchMatches.length > 0) {
        groups.push({
          key: "search-all",
          label: "ALL LISTS",
          items: globalSearchMatches,
        });
      }
      return groups;
    }

    if (activeCategory === "all" && s === "") {
      const groups: DisplayGroup[] = [];
      const shownIds = new Set<string>();

      const favItems = favoriteOptions.filter((o) =>
        tabSearchMatches.some((m) => m.id === o.id),
      );
      if (favItems.length > 0) {
        groups.push({ key: "quick_picks", label: "FAVORITES", items: favItems });
        favItems.forEach((o) => shownIds.add(o.id));
      }

      const trendingItems = trendingAll.filter((o) => !shownIds.has(o.id));
      if (trendingItems.length > 0) {
        groups.push({
          key: "trending",
          label: "TRENDING",
          items: trendingItems,
          isTrending: true,
        });
        trendingItems.forEach((o) => shownIds.add(o.id));
      }

      const restOptions = tabSearchMatches.filter((o) => !shownIds.has(o.id));
      groups.push(
        ...groupedOptions(restOptions, [], trendingByCategory, true),
      );
      return groups;
    }

    return groupedOptions(
      tabSearchMatches,
      trendingAll,
      trendingByCategory,
      activeCategory === "all",
    );
  }, [
    searchTerm,
    tabSearchMatches,
    globalSearchMatches,
    activeCategory,
    favoriteOptions,
    trendingAll,
    trendingByCategory,
  ]);

  const visibleTabs = useMemo(() => {
    const tabs: { key: PickerCategory; label: string; icon?: React.ReactNode }[] = [];
    tabs.push({ key: "all", label: "All" });
    tabs.push(
      { key: "custom", label: "Lists" },
      { key: "countries", label: "Countries" },
      { key: "sectors", label: "Sectors" },
      { key: "portfolios", label: "Portfolios" },
      { key: "watchlists", label: "Watchlists" },
    );
    return tabs;
  }, []);

  const isChip = triggerVariant === "listChip";
  const isToolbar = triggerVariant === "toolbar";
  const isMedium = triggerVariant === "medium";
  const isCompact = isChip || isMedium || isToolbar;
  const iconSlot = triggerFixedWidth ? 11 : isChip || isToolbar ? 11 : isMedium ? 16 : 20;
  const textSize = isChip ? 12 : isToolbar || isMedium ? HEATMAP_TOOLBAR_CHIP_FONT_SIZE : 20;
  const textWeight = isChip || isToolbar ? 500 : isMedium ? 500 : 600;

  const canOpenPopover = !!(renderTrigger || selected || selectedId === "");

  const handleTriggerClick = useCallback(() => {
    if (readOnlyTrigger) return;
    // Optional external intercept: when it returns true, the caller has handled
    // the click (e.g. toggling a separate favorites row) and the popover must
    // not open. Used by the Hub/Radar quick-list second row.
    if (interceptTriggerClick?.()) return;
    if (requireAuth) {
      onRequireAuth?.();
      return;
    }
    if (openOnHover) {
      handleHoverToggleClick();
      return;
    }
    // `selectedId === ""` = explicit empty slot (not loading); still openable.
    if (renderTrigger || selected || selectedId === "") setOpen((v) => !v);
  }, [
    readOnlyTrigger,
    interceptTriggerClick,
    requireAuth,
    onRequireAuth,
    openOnHover,
    handleHoverToggleClick,
    renderTrigger,
    selected,
    selectedId,
    setOpen,
  ]);

  const handlePickerTriggerEnter = useCallback(() => {
    if (!canOpenPopover) return;
    handleTriggerEnter();
  }, [canOpenPopover, handleTriggerEnter]);

  const combineTheme = COMBINE_OP_THEME[combineOp];

  const openProPaywall = useCallback(() => {
    if (onShowPro) onShowPro();
    else combine?.onShowPro?.();
  }, [onShowPro, combine]);

  const isPortfolioProLocked = useCallback(
    (option: PickerOption) =>
      !isBenchmarkCollectionId(option.id) &&
      (normalizeListType(option.type) === "portfolio" || option.id === ALL_PORTFOLIOS_PICKER_ID) &&
      !isPaid,
    [isPaid],
  );

  const handlePickerOptionClick = useCallback(
    (option: PickerOption) => {
      if (isPortfolioProLocked(option)) {
        openProPaywall();
        return;
      }
      if (combineMode && combine) {
        if (!isPaid) {
          combine.onShowPro?.();
          return;
        }
        const parsed = combine.parseLogicSource(option.id, option.label);
        combine.addFilter(parsed.type, parsed.value, parsed.label, combineOp);
        setSearchTerm("");
        return;
      }
      const activeUniverse = option.universe;
      trackRecent(option.id);
      if (activeUniverse) {
        onSelect(activeUniverse);
        if (!keepOpenOnSelect) setOpen(false);
        return;
      }
      if (option.id.startsWith("preset:")) {
        onSelect({
          id: option.id,
          label: option.label,
          emoji: option.emoji,
          count: option.count ?? 0,
          locked: false,
          tickers: [],
          type: option.type,
        });
        if (!keepOpenOnSelect) setOpen(false);
        return;
      }
      onAddList(option.id);
      if (!keepOpenOnSelect) {
        setSearchTerm("");
        setOpen(false);
      }
    },
    [
      isPortfolioProLocked,
      openProPaywall,
      combineMode,
      combine,
      isPaid,
      combineOp,
      trackRecent,
      onSelect,
      keepOpenOnSelect,
      onAddList,
      setOpen,
    ],
  );

  const openAfterTrigger = open && afterTrigger ? afterTrigger : null;
  const effectiveAfterTrigger = readOnlyTrigger ? null : openAfterTrigger;
  const showChevron = !readOnlyTrigger;
  const quickRowOpen = triggerChevronOpen === true;
  const chevronOpen = quickRowOpen || open;

  return (
    <div
      className="hub-picker"
      style={{
        position: "relative",
        display: "inline-block",
        zIndex: open ? 250 : undefined,
      }}
    >
      {/* ──────────────── Trigger ──────────────── */}
      {renderTrigger
        ? renderTrigger({
            onClick: handleTriggerClick,
            open: requireAuth ? false : open,
            ref: triggerRef,
            afterTrigger: effectiveAfterTrigger,
            onTriggerEnter: handlePickerTriggerEnter,
            onTriggerLeave: handleTriggerLeave,
          })
        : null}
      {!renderTrigger && <button
        ref={(node) => {
          triggerRef.current = node;
        }}
        className="hub-picker-trigger"
        type="button"
        onClick={handleTriggerClick}
        aria-haspopup={requireAuth ? undefined : "listbox"}
        aria-expanded={requireAuth ? undefined : chevronOpen}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: isToolbar ? 4 : isChip ? 6 : isMedium ? 8 : 12,
          ...(triggerFixedWidth
            ? { width: triggerFixedWidth, minWidth: triggerFixedWidth, maxWidth: triggerFixedWidth }
            : {}),
          ...(isToolbar
            ? heatmapToolbarTriggerDims()
            : {
                padding: isChip
                  ? "2px 10px 2px 8px"
                  : isMedium
                    ? "6px 14px 6px 12px"
                    : "12px 18px 12px 16px",
                minHeight: isChip ? 28 : isMedium ? 36 : undefined,
                borderRadius: isCompact ? 10 : 12,
              }),
          ...(triggerFixedWidth ? { justifyContent: "flex-start", overflow: "hidden" } : {}),
          ...(readOnlyTrigger
            ? {
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.1)",
                boxShadow: "none",
              }
            : heroPickerTriggerOpenStyle("medium", open, quickRowOpen)),
          color: "#fff",
          fontFamily: FONT_BASTION,
          cursor: readOnlyTrigger ? "default" : "pointer",
          transition: "background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
        }}
        {...heroPickerTriggerHoverHandlers("medium", open, readOnlyTrigger, quickRowOpen)}
      >
        {triggerLabelPrefix ? (
          <span
            className="hub-picker-trigger-prefix"
            style={{
              fontSize: textSize,
              fontWeight: 500,
              color: "rgba(255,255,255,0.45)",
              letterSpacing: "-0.01em",
              lineHeight: 1,
              flexShrink: 0,
              marginRight: isToolbar ? 6 : 0,
            }}
          >
            {triggerLabelPrefix}
          </span>
        ) : null}
        {selected ? (
          <>
            {(() => {
              const opt = options.find((o) => o.id === selected.id);
              const iconEl = (
                <span
                  style={{
                    fontSize: iconSlot,
                    lineHeight: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {renderPickerListIcon({
                    id: selected.id,
                    type: opt?.type ?? selected.type,
                    strategyCode: opt?.strategyCode,
                    emoji: selected.emoji,
                    size: iconSlot,
                  })}
                </span>
              );
              const labelStyle: React.CSSProperties = {
                fontSize: textSize,
                fontWeight: textWeight,
                color: "#fff",
                letterSpacing: "-0.01em",
                lineHeight: 1,
              };
              const countEl =
                triggerCount != null && triggerCount > 0 ? (
                  <span
                    aria-label={`${triggerCount} stocks`}
                    style={{
                      fontSize: textSize,
                      fontWeight: 500,
                      color: "rgba(255,255,255,0.42)",
                      lineHeight: 1,
                      flexShrink: 0,
                      marginLeft: 6,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {triggerCount.toLocaleString()}
                  </span>
                ) : null;
              if (triggerFixedWidth) {
                // Reserve room for icon + gap + chevron + paddings so the
                // label truncates instead of stretching/centering. Fixed width
                // minus ~icon(20) + gap(8) + chevron(14) + padding(26) ≈ 68px.
                // The stock count keeps its digits; the list name truncates instead.
                const countReserve =
                  triggerCount != null && triggerCount > 0
                    ? 6 + String(triggerCount.toLocaleString()).length * Math.ceil(textSize * 0.62)
                    : 0;
                const labelMaxWidth = triggerFixedWidth - 72 - countReserve;
                return (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: isToolbar ? 4 : isMedium ? 8 : 12,
                      minWidth: 0,
                      flex: "0 1 auto",
                      overflow: "hidden",
                    }}
                  >
                    {iconEl}
                    <PickerFixedWidthLabel
                      label={selected.label}
                      labelStyle={labelStyle}
                      labelMaxWidth={labelMaxWidth}
                    />
                    {countEl}
                  </span>
                );
              }
              return (
                <>
                  {iconEl}
                  <span className="hub-picker-trigger-label" style={labelStyle}>{selected.label}</span>
                  {countEl}
                </>
              );
            })()}
          </>
        ) : selectedId === "" ? (
          <PickerEmptyState label="Select list" size={textSize} weight={textWeight} />
        ) : (
          <PickerLoadingState />
        )}
        <span
          style={{
            marginLeft: showChevron ? (triggerFixedWidth ? "auto" : 2) : 0,
            flexShrink: 0,
            display: "inline-flex",
            alignItems: "center",
            overflow: "hidden",
            opacity: showChevron ? 1 : 0,
            maxWidth: showChevron ? 16 : 0,
            transition: "opacity 0.28s cubic-bezier(0.4,0,0.2,1), max-width 0.28s cubic-bezier(0.4,0,0.2,1), margin 0.28s cubic-bezier(0.4,0,0.2,1)",
          }}
        >
          <ChevronCaret open={chevronOpen} color={heroPickerTriggerChevronColor(open, quickRowOpen)} />
        </span>
        {effectiveAfterTrigger ? (
          <span
            style={{
              flexShrink: 0,
              display: "inline-flex",
              alignItems: "center",
              marginLeft: 6,
            }}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {effectiveAfterTrigger}
          </span>
        ) : null}
      </button>}

      {/* ──────────────── Popover ──────────────── */}
      {!requireAuth && mounted && open && !readOnlyTrigger && popoverLayout && createPortal(
        <>
          {popoverLayout.isMobile && <HeroPickerPopoverBackdrop onClose={() => setOpen(false)} />}
          <div
          ref={popoverRef}
          role="listbox"
          onMouseEnter={handlePopoverEnter}
          onMouseLeave={handlePopoverLeave}
          style={{
            position: popoverLayout.position,
            top: popoverLayout.top,
            left: popoverLayout.left,
            width: popoverLayout.width,
            height: popoverLayout.maxHeight,
            maxHeight: popoverLayout.maxHeight,
            background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
            border: combineMode ? `1px solid ${combineTheme.popoverBorder}` : "1px solid #444",
            borderRadius: 12,
            boxShadow: combineMode ? combineTheme.popoverShadow : "0 20px 60px rgba(0, 0, 0, 0.5)",
            zIndex: 10000,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            fontFamily: FONT_BASTION,
            transition: "border-color 0.2s ease, box-shadow 0.2s ease",
            ...(openOnHover && hoverCapable ? popoverEnterStyle(popoverEntered) : {}),
          }}
        >
          {popoverHeader}

          {/* Combine — "+ Combine" entry or AND/OR/NOT banner while active */}
          {combine ? (
            combineMode ? (
              <div
                style={{
                  flexShrink: 0,
                  background: combineTheme.bannerBg,
                  borderBottom: `1px solid ${combineTheme.bannerBorder}`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "12px 16px",
                  }}
                >
                  <span
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: combineTheme.labelColor,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Combine
                  </span>
                  <CombineOperatorTabs op={combineOp} onChange={setCombineOp} />
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 11,
                      color: "rgba(255,255,255,0.4)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {COMBINE_OP_HINT[combineOp]}
                  </span>
                  {combineAccessory}
                  <button
                    type="button"
                    onClick={() => setCombineMode(false)}
                    aria-label="Stop combining lists"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 26,
                      height: 26,
                      flexShrink: 0,
                      padding: 0,
                      borderRadius: 6,
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(0,0,0,0.2)",
                      color: "rgba(255,255,255,0.5)",
                      cursor: "pointer",
                      transition: "color 0.15s ease, background 0.15s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "rgba(255,255,255,0.85)";
                      e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = "rgba(255,255,255,0.5)";
                      e.currentTarget.style.background = "rgba(0,0,0,0.2)";
                    }}
                  >
                    <X size={13} strokeWidth={2.2} />
                  </button>
                </div>
              </div>
            ) : (
              <div
                style={{
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  padding: popoverLayout.isMobile ? "10px 12px" : "12px 16px",
                  borderBottom: "1px solid #333",
                  backgroundColor: "#1a1a1a",
                }}
              >
                <button
                  type="button"
                  onClick={handleCombineToggle}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 12px",
                    borderRadius: 8,
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(255,255,255,0.04)",
                    color: "rgba(255,255,255,0.72)",
                    fontSize: 12,
                    fontWeight: 500,
                    fontFamily: FONT_BASTION,
                    cursor: "pointer",
                    flexShrink: 0,
                    transition: "background 0.15s ease, border-color 0.15s ease, color 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)";
                    e.currentTarget.style.color = "rgba(255,255,255,0.9)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
                    e.currentTarget.style.color = "rgba(255,255,255,0.72)";
                  }}
                >
                  <Plus size={14} strokeWidth={2.2} />
                  Combine
                  {!isPaid && (
                    <span
                      style={{
                        fontFamily: 'var(--font-jetbrains), ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        fontSize: 8.5,
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        color: "#DCE2EC",
                        background: "rgba(220,226,236,0.10)",
                        border: "1px solid rgba(220,226,236,0.22)",
                        borderRadius: 4,
                        padding: "1px 4px",
                        lineHeight: 1.2,
                        marginLeft: 2,
                        flexShrink: 0,
                      }}
                    >
                      PRO
                    </span>
                  )}
                </button>
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 11,
                    lineHeight: 1.3,
                    color: "rgba(255,255,255,0.4)",
                    fontFamily: FONT_BASTION,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  Build advanced lists, e.g. S&amp;P 500 + Technology
                </span>
                {combineAccessory}
              </div>
            )
          ) : null}

          {/* Search */}
          <div
            style={{
              padding: popoverLayout.isMobile ? "12px 12px 10px 12px" : "20px 20px 16px 20px",
              borderBottom: "1px solid #333",
              backgroundColor: "#1a1a1a",
              flexShrink: 0,
            }}
          >
            {popoverPrefix}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                backgroundColor: "#2a2a2a",
                border: "1px solid #555",
                borderRadius: 8,
                padding: "10px 14px",
              }}
            >
              <Search size={13} style={{ color: "rgba(255,255,255,0.35)", flexShrink: 0 }} />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={combineMode ? "Search lists to combine…" : "Search lists…"}
                autoFocus
                style={{
                  flex: 1,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  color: "#fff",
                  fontSize: 13,
                  fontFamily: FONT_BASTION,
                }}
              />
              <button
                onClick={() => {
                  if (searchTerm) { setSearchTerm(""); }
                  else { setOpen(false); }
                }}
                aria-label="Close"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 20,
                  height: 20,
                  borderRadius: 4,
                  border: "none",
                  background: "transparent",
                  color: "rgba(255,255,255,0.35)",
                  cursor: "pointer",
                  flexShrink: 0,
                  padding: 0,
                  transition: "color 0.15s, background 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "rgba(255,255,255,0.8)";
                  e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "rgba(255,255,255,0.35)";
                  e.currentTarget.style.background = "transparent";
                }}
              >
                <X size={14} strokeWidth={2} />
              </button>
            </div>

            <PickerTabsRow
              tabs={visibleTabs}
              activeCategory={activeCategory}
              onSelect={(key) => setActiveCategory(key)}
            />
          </div>

          {/* Body — scrollable */}
          <div className="custom-dropdown-scrollbar" style={{ flex: 1, overflowY: "auto", minHeight: 0, scrollbarWidth: "thin", scrollbarColor: "#3d3d3d #1a1a1a" }}>
            {searchTerm.trim() === "" && activeCategory === "watchlists" && !universes.some((u) => u.id === "shelf") && (
              <BuildShelfCta user={user} onSignUp={onSignUp} />
            )}

            {displayGroups.length === 0 ? (
              <div
                style={{
                  padding: "16px 14px",
                  color: "rgba(255,255,255,0.4)",
                  fontSize: 12,
                  textAlign: "center",
                }}
              >
                No matching lists
              </div>
            ) : (
              displayGroups.map((group) => (
                <div key={group.key}>
                  {(group.items.length > 0 || !group.moreItems?.length) ? (
                    <SectionLabel>{group.label}</SectionLabel>
                  ) : null}
                  {group.items.map((option) => (
                    <PickerRow
                      key={`${group.key}:${option.type}:${option.id}`}
                      option={option}
                      active={option.id === selectedId}
                      locked={isPortfolioProLocked(option)}
                      showProBadge={isPortfolioProLocked(option)}
                      showMomentum
                      isPinnedList={!!quickPickIds?.includes(option.id)}
                      canManageQuickAccess={canManageQuickAccess}
                      onAddQuickAccessList={onAddQuickAccessList}
                      onRemoveQuickAccessList={onRemoveQuickAccessList}
                      onClick={() => handlePickerOptionClick(option)}
                    />
                  ))}
                  {group.moreItems && group.moreItems.length > 0 ? (
                    <>
                      {group.items.length === 0 ? (
                        <SectionLabel>{group.label}</SectionLabel>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => {
                          setExpandedMoreGroupKeys((prev) => {
                            const next = new Set(prev);
                            if (next.has(group.key)) next.delete(group.key);
                            else next.add(group.key);
                            return next;
                          });
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          width: "100%",
                          padding: "7px 14px 7px 16px",
                          border: "none",
                          background: "transparent",
                          color: "rgba(255,255,255,0.42)",
                          fontSize: 11.5,
                          fontWeight: 500,
                          fontFamily: FONT_BASTION,
                          cursor: "pointer",
                          textAlign: "left",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "rgba(255,255,255,0.65)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "rgba(255,255,255,0.42)";
                        }}
                      >
                        <ChevronRight
                          size={12}
                          strokeWidth={2}
                          style={{
                            flexShrink: 0,
                            opacity: 0.7,
                            transform: expandedMoreGroupKeys.has(group.key)
                              ? "rotate(90deg)"
                              : "rotate(0deg)",
                            transition: "transform 0.15s ease",
                          }}
                        />
                        More ({group.moreItems.length})
                      </button>
                      {expandedMoreGroupKeys.has(group.key)
                        ? group.moreItems.map((option) => (
                            <PickerRow
                              key={`${group.key}:more:${option.type}:${option.id}`}
                              option={option}
                              active={option.id === selectedId}
                              locked={isPortfolioProLocked(option)}
                              showProBadge={isPortfolioProLocked(option)}
                              showMomentum
                              isPinnedList={!!quickPickIds?.includes(option.id)}
                              canManageQuickAccess={canManageQuickAccess}
                              onAddQuickAccessList={onAddQuickAccessList}
                              onRemoveQuickAccessList={onRemoveQuickAccessList}
                              onClick={() => handlePickerOptionClick(option)}
                            />
                          ))
                        : null}
                    </>
                  ) : null}
                </div>
              ))
            )}
          </div>

          
        </div>
        </>,
        document.body,
      )}
    </div>
  );
}

/* ─────────────────────────── helpers ─────────────────────────── */

function ChevronCaret({ open, color = "rgba(255,255,255,0.55)" }: { open: boolean; color?: string }) {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      style={{
        marginLeft: 2,
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
        transition: "transform 0.22s cubic-bezier(0.4,0,0.2,1), color 0.2s ease",
        color,
        flexShrink: 0,
      }}
      aria-hidden
    >
      <path d="M2 3.5 L5 6.5 L8 3.5" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SectionLabel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div
      style={{
        padding: "12px 14px 4px",
        fontSize: 9.5,
        fontWeight: 600,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: "rgba(255,255,255,0.35)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function normalizeListType(type: string | undefined): string {
  const t = String(type || "list").toLowerCase().replace(/[_\s-]/g, "");
  if (t === "country" || t === "countries") return "country";
  if (t === "sector" || t === "sectors") return "sector";
  if (t === "industrygroup" || t === "industrygroups") return "industrygroup";
  if (t === "industry" || t === "industries") return "industry";
  if (t === "portfolio" || t === "portfolios") return "portfolio";
  if (t === "watchlist" || t === "watchlists") return "watchlist";
  return "list";
}

function categoryForType(type: string): PickerCategory {
  if (type === "country") return "countries";
  if (type === "sector" || type === "industrygroup" || type === "industry") return "sectors";
  if (type === "portfolio") return "portfolios";
  if (type === "watchlist") return "watchlists";
  return "custom";
}

function sectorSubtype(type: string): "sector" | "industrygroup" | "industry" | null {
  if (type === "sector") return "sector";
  if (type === "industrygroup") return "industrygroup";
  if (type === "industry") return "industry";
  return null;
}

const SECTOR_SUBTYPE_ORDER: ("sector" | "industrygroup" | "industry")[] = ["sector", "industrygroup", "industry"];
const SECTOR_SUBTYPE_LABELS: Record<string, string> = {
  sector: "SECTORS",
  industrygroup: "INDUSTRY GROUPS",
  industry: "INDUSTRIES",
};

const CATEGORY_LABELS: Record<PickerCategory, string> = {
  quick_picks: "Quick Picks",
  all: "All",
  recent: "Recent",
  trending: "Trending",
  custom: "Lists",
  countries: "Countries",
  sectors: "Sectors",
  portfolios: "Portfolios",
  watchlists: "Watchlists",
};

const CATEGORY_ORDER: PickerCategory[] = [
  "custom",
  "countries",
  "sectors",
  "portfolios",
  "watchlists",
];

const BENCHMARK_ALL_PICKER_ID = "bench:all";

const PICKER_MUTED_ICON_COLOR = "rgba(255,255,255,0.75)";

const PICKER_BENCHMARK_ICON: LucideIcon = BarChart3;

function renderPickerListIcon({
  id,
  type,
  strategyCode,
  emoji,
  size,
}: {
  id: string;
  type?: string;
  strategyCode?: string;
  emoji?: string;
  size: number;
}): ReactNode {
  if (strategyCode) {
    const Icon = getStrategyIconForCode(strategyCode);
    const isPortfolio =
      normalizeListType(type) === "portfolio" || id === ALL_PORTFOLIOS_PICKER_ID;
    const color = isPortfolio
      ? PICKER_MUTED_ICON_COLOR
      : STRATEGY_COLORS[strategyCode] || "#fff";
    if (Icon) return <Icon size={size} style={{ color }} />;
  }
  if (normalizeListType(type) === "watchlist" || id === "shelf") {
    return <Star size={size} fill="currentColor" style={{ color: PICKER_MUTED_ICON_COLOR }} />;
  }
  if (id.startsWith("preset:")) {
    return <GitMerge size={size} strokeWidth={2} style={{ color: PICKER_MUTED_ICON_COLOR }} />;
  }
  if (isBenchmarkCollectionId(id)) {
    const Icon = PICKER_BENCHMARK_ICON;
    return <Icon size={size} strokeWidth={2} style={{ color: PICKER_MUTED_ICON_COLOR }} />;
  }
  return <span style={{ fontSize: Math.max(11, Math.round(size * 0.95)), lineHeight: 1 }}>{emoji}</span>;
}

/** Synthetic aggregate of every portfolio holding — picker UX only (DB type stays `list`). */
const ALL_PORTFOLIOS_PICKER_ID = "allportfolios";

function resolvePickerListType(id: string, type?: string): string {
  if (id === ALL_PORTFOLIOS_PICKER_ID) return "portfolio";
  return canonicalPickerListType(id, normalizeListType(type));
}

const PORTFOLIO_REGION_ORDER: RegionId[] = ["us", "europe", "global"];
const PORTFOLIO_REGION_LABELS: Record<RegionId, string> = {
  us: "US",
  europe: "EUROPE",
  global: "GLOBAL",
};

function isPortfolioPickerOption(o: PickerOption): boolean {
  return (
    normalizeListType(o.type) === "portfolio" ||
    o.id === ALL_PORTFOLIOS_PICKER_ID ||
    o.id === BENCHMARK_PORTFOLIO_COLLECTION_ID
  );
}

function isPortfolioBenchmarkOption(o: PickerOption): boolean {
  if (o.id === BENCHMARK_PORTFOLIO_COLLECTION_ID) return false;
  if (!isPortfolioPickerOption(o)) return false;
  if (o.id === ALL_PORTFOLIOS_PICKER_ID) return false;
  const label = (o.label || "").toLowerCase();
  const id = o.id.toLowerCase();
  if (/benchmark/.test(label)) return true;
  const code = id.startsWith("portfolio:") ? id.slice("portfolio:".length) : id;
  return /benchmark/.test(code);
}

function portfolioRegionOf(option: PickerOption): RegionId {
  return regionOfList(option.listId, option.label);
}

function pushPortfolioSubgroups(
  items: PickerOption[],
  groups: PickerOptionGroup[],
  trendItems?: PickerOption[],
) {
  const portfolios = items.filter(
    (o) => isPortfolioPickerOption(o) && !isPortfolioBenchmarkOption(o),
  );
  if (trendItems?.length) {
    groups.push({
      key: "trending-portfolios",
      label: "TRENDING",
      items: trendItems,
      isTrending: true,
    });
  }

  const allPortfolios = portfolios.filter((o) => o.id === ALL_PORTFOLIOS_PICKER_ID);
  const portfolioBenchmarks = portfolios.filter((o) => o.id === BENCHMARK_PORTFOLIO_COLLECTION_ID);
  const rest = portfolios.filter(
    (o) => o.id !== ALL_PORTFOLIOS_PICKER_ID && o.id !== BENCHMARK_PORTFOLIO_COLLECTION_ID,
  );

  if (allPortfolios.length > 0 || portfolioBenchmarks.length > 0) {
    groups.push({
      key: "portfolios-all",
      label: "ALL",
      items: [...allPortfolios, ...portfolioBenchmarks],
    });
  }

  for (const region of PORTFOLIO_REGION_ORDER) {
    const regionItems = dedupeByLabel(
      sortByCount(rest.filter((o) => portfolioRegionOf(o) === region)),
    );
    if (regionItems.length > 0) {
      groups.push({
        key: `portfolios-${region}`,
        label: PORTFOLIO_REGION_LABELS[region],
        items: regionItems,
      });
    }
  }
}

function isBenchmarkPickerOption(o: PickerOption): boolean {
  return o.id.startsWith("bench:") && o.id !== BENCHMARK_PORTFOLIO_COLLECTION_ID;
}

function isEtfPickerOption(o: PickerOption): boolean {
  return o.id.toLowerCase().includes("etf") || o.label.toLowerCase().includes("etf");
}

function sortBenchmarkPickerOptions(items: PickerOption[]): PickerOption[] {
  return [...items].sort((a, b) => {
    if (a.id === BENCHMARK_ALL_PICKER_ID) return -1;
    if (b.id === BENCHMARK_ALL_PICKER_ID) return 1;
    return a.label.localeCompare(b.label);
  });
}

/** Split Lists-tab items into LISTS / BENCHMARKS / ETF sections (same pattern as ETF). */
function pushCustomListSubgroups(
  items: PickerOption[],
  groups: { key: string; label: string; items: PickerOption[]; isTrending?: boolean }[],
) {
  const benchItems = sortBenchmarkPickerOptions(items.filter(isBenchmarkPickerOption));
  const etfItems = items.filter((o) => !isBenchmarkPickerOption(o) && isEtfPickerOption(o));
  const listItems = items.filter((o) => !isBenchmarkPickerOption(o) && !isEtfPickerOption(o));
  if (listItems.length > 0) {
    groups.push({ key: "custom", label: "LISTS", items: listItems });
  }
  if (benchItems.length > 0) {
    groups.push({ key: "benchmarks", label: "BENCHMARKS", items: benchItems });
  }
  if (etfItems.length > 0) {
    groups.push({ key: "etf", label: "ETF", items: etfItems });
  }
}

function searchSectionLabel(category: PickerCategory): string {
  if (category === "quick_picks") return "FAVORITES";
  if (category === "recent") return "RECENT";
  if (category === "all") return "ALL";
  return CATEGORY_LABELS[category];
}

function dedupeByLabel(items: PickerOption[]): PickerOption[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = item.label.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function sortByCount(items: PickerOption[]) {
  return [...items].sort((a, b) => {
    const aPin = a.id === "all" ? 1 : 0;
    const bPin = b.id === "all" ? 1 : 0;
    if (aPin !== bPin) return bPin - aPin;
    return (b.count ?? 0) - (a.count ?? 0);
  });
}

/** Countries / sectors tabs — hide thin universes from the main list. */
const MIN_GEO_SECTOR_PICKER_COUNT = 7;

type PickerOptionGroup = {
  key: string;
  label: string;
  items: PickerOption[];
  isTrending?: boolean;
  moreItems?: PickerOption[];
};

function splitGeoSectorByMinCount(items: PickerOption[]): {
  visible: PickerOption[];
  more: PickerOption[];
} {
  const visible: PickerOption[] = [];
  const more: PickerOption[] = [];
  for (const item of items) {
    if ((item.count ?? 0) >= MIN_GEO_SECTOR_PICKER_COUNT) visible.push(item);
    else more.push(item);
  }
  return { visible, more };
}

function pushGeoSectorGroup(
  groups: PickerOptionGroup[],
  key: string,
  label: string,
  items: PickerOption[],
  isTrending?: boolean,
) {
  const { visible, more } = splitGeoSectorByMinCount(items);
  if (visible.length === 0 && more.length === 0) return;
  groups.push({
    key,
    label,
    items: visible,
    isTrending,
    moreItems: more.length > 0 ? more : undefined,
  });
}

function groupedOptions(
  options: PickerOption[],
  trending: PickerOption[],
  trendingByCategory: Record<string, PickerOption[]>,
  showAllCategories: boolean,
) {
  const groups: PickerOptionGroup[] = [];

  const pushSectorSubgroups = (items: PickerOption[], trendItems?: PickerOption[]) => {
    if (trendItems && trendItems.length > 0) {
      groups.push({ key: "trending-sectors", label: "TRENDING", items: trendItems, isTrending: true });
    }
    for (const sub of SECTOR_SUBTYPE_ORDER) {
      const subItems = dedupeByLabel(sortByCount(items.filter((o) => normalizeListType(o.type) === sub)));
      pushGeoSectorGroup(groups, `sectors-${sub}`, SECTOR_SUBTYPE_LABELS[sub], subItems);
    }
  };

  if (showAllCategories) {
    if (trending.length > 0) {
      groups.push({ key: "trending", label: "TRENDING", items: trending, isTrending: true });
    }
    for (const key of CATEGORY_ORDER) {
      const items = dedupeByLabel(sortByCount(options.filter((o) => categoryForType(o.type) === key)));
      if (items.length > 0) {
        if (key === "sectors") {
          pushSectorSubgroups(items);
        } else if (key === "custom") {
          pushCustomListSubgroups(items, groups);
        } else if (key === "portfolios") {
          pushPortfolioSubgroups(items, groups);
        } else if (key === "countries") {
          pushGeoSectorGroup(groups, key, CATEGORY_LABELS[key], items);
        } else {
          groups.push({ key, label: CATEGORY_LABELS[key], items });
        }
      }
    }
  } else {
    const catKey = CATEGORY_ORDER.find((k) =>
      options.some((o) => categoryForType(o.type) === k),
    );
    if (catKey) {
      const catTrending = dedupeByLabel(trendingByCategory[catKey] || []);
      if (catKey === "sectors") {
        const items = dedupeByLabel(sortByCount(options));
        pushSectorSubgroups(items, catTrending);
      } else if (catKey === "custom") {
        if (catTrending.length > 0) {
          groups.push({ key: `trending-${catKey}`, label: "TRENDING", items: catTrending, isTrending: true });
        }
        const items = dedupeByLabel(sortByCount(options));
        pushCustomListSubgroups(items, groups);
      } else if (catKey === "portfolios") {
        const items = dedupeByLabel(sortByCount(options));
        pushPortfolioSubgroups(items, groups, catTrending);
      } else {
        if (catTrending.length > 0) {
          groups.push({ key: `trending-${catKey}`, label: "TRENDING", items: catTrending, isTrending: true });
        }
        const items = dedupeByLabel(sortByCount(options));
        if (catKey === "countries") {
          pushGeoSectorGroup(groups, catKey, CATEGORY_LABELS[catKey], items);
        } else if (items.length > 0) {
          groups.push({ key: catKey, label: CATEGORY_LABELS[catKey], items });
        }
      }
    }
  }

  return groups;
}


const PICKER_PRO_BADGE_STYLE: CSSProperties = {
  fontFamily: 'var(--font-jetbrains), ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 8.5,
  fontWeight: 700,
  letterSpacing: "0.08em",
  color: "#DCE2EC",
  background: "rgba(220,226,236,0.10)",
  border: "1px solid rgba(220,226,236,0.22)",
  borderRadius: 4,
  padding: "1px 4px",
  lineHeight: 1.2,
  flexShrink: 0,
};

const PICKER_ROW_LABEL_IDLE = "rgba(255,255,255,0.88)";
const PICKER_ROW_LABEL_HOVER = "rgba(255,255,255,0.96)";
const PICKER_ROW_COUNT_COLOR = "rgba(255,255,255,0.52)";

function PickerRowQuickAccessPin({
  pinned,
  onToggle,
}: {
  pinned: boolean;
  onToggle: () => void;
}) {
  const restColor = pinned ? BASTION_GOLD : "rgba(255,255,255,0.48)";
  const restBg = pinned ? BASTION_GOLD_SOFT : "transparent";
  const hoverColor = pinned ? "#ffe566" : "rgba(255,255,255,0.92)";
  const hoverBg = pinned ? BASTION_GOLD_SOFT_HOVER : "rgba(255,255,255,0.10)";

  return (
    <span
      role="button"
      tabIndex={0}
      title={pinned ? "Remove from quick access" : "Add to quick access"}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }}
      style={{
        width: 18,
        height: 18,
        borderRadius: 5,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: restColor,
        background: restBg,
        flexShrink: 0,
        fontSize: 13,
        fontWeight: 700,
        cursor: "pointer",
        transition: "color 0.15s ease, background 0.15s ease, transform 0.15s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = hoverColor;
        e.currentTarget.style.background = hoverBg;
        e.currentTarget.style.transform = "scale(1.12)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = restColor;
        e.currentTarget.style.background = restBg;
        e.currentTarget.style.transform = "scale(1)";
      }}
    >
      {pinned ? "★" : "+"}
    </span>
  );
}

function PickerRow({
  option,
  active,
  locked,
  showProBadge = false,
  showMomentum,
  isPinnedList = false,
  canManageQuickAccess = false,
  onAddQuickAccessList,
  onRemoveQuickAccessList,
  onClick,
}: {
  option: PickerOption;
  active: boolean;
  locked: boolean;
  showProBadge?: boolean;
  showMomentum?: boolean;
  isPinnedList?: boolean;
  canManageQuickAccess?: boolean;
  onAddQuickAccessList?: (pickerId: string) => void;
  onRemoveQuickAccessList?: (itemId: string) => void;
  onClick: () => void;
}) {
  const quickAccessItemId = option.quickAccessItemId;
  const pinned = isPinnedList || !!quickAccessItemId;
  const rowColor = active ? PICKER_ACCENT_TEXT : PICKER_ROW_LABEL_IDLE;

  return (
    <button
      type="button"
      onClick={onClick}
      role="option"
      aria-selected={active}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        width: "100%",
        padding: "8px 12px",
        border: "none",
        ...pickerOptionRowSelectionStyle(active),
        color: rowColor,
        cursor: "pointer",
        fontSize: 12,
        textAlign: "left",
        transition: "background-color 0.2s ease, color 0.2s ease",
        fontFamily: FONT_BASTION,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = pickerOptionRowHoverBg(active, "enter");
        if (!active) e.currentTarget.style.color = PICKER_ROW_LABEL_HOVER;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = pickerOptionRowHoverBg(active, "leave");
        e.currentTarget.style.color = rowColor;
      }}
    >
      <span style={{ width: 22, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {renderPickerListIcon({
          id: option.id,
          type: option.type,
          strategyCode: option.strategyCode,
          emoji: option.emoji,
          size: 14,
        })}
      </span>
      <span style={{ flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontWeight: active ? 600 : 400, display: "flex", alignItems: "center", gap: 5 }}>
        {normalizeListType(option.type) === "portfolio" && (
          <MiniRegionFlag region={regionOfList(option.listId, option.label)} />
        )}
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
          {option.label}
          {option.count != null && (
            <span style={{ color: PICKER_ROW_COUNT_COLOR, fontWeight: 400 }}> ({option.count.toLocaleString()})</span>
          )}
        </span>
      </span>
      {showProBadge ? (
        <span style={PICKER_PRO_BADGE_STYLE}>PRO</span>
      ) : null}
      {(() => {
        const isEtfList = option.id.toLowerCase().includes("etf") || option.label.toLowerCase().includes("etf");
        if (isEtfList || showProBadge) return null;
        const minForBar = normalizeListType(option.type) === "portfolio" ? 4 : 7;
          return option.momentum != null && (option.count ?? 0) >= minForBar && (
            <BastionTooltip
              text={`Price Momentum: ${Math.round(option.momentum)} / 100`}
              zIndex={10001}
            >
              <MomentumBars score={option.momentum} />
            </BastionTooltip>
          );
      })()}
      {canManageQuickAccess && (
        <PickerRowQuickAccessPin
          pinned={pinned}
          onToggle={() => {
            if (quickAccessItemId) onRemoveQuickAccessList?.(quickAccessItemId);
            else onAddQuickAccessList?.(option.id);
          }}
        />
      )}
    </button>
  );
}

function BuildShelfCta({ user, onSignUp }: { user: any; onSignUp: () => void }) {
  if (user) {
    return (
      <Link
        href="/watchlist"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "10px 14px",
          color: "rgba(159,232,112,0.85)",
          textDecoration: "none",
          fontSize: 13,
          fontFamily: FONT_BASTION,
          transition: "background 0.1s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "rgba(159,232,112,0.06)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
        }}
      >
        <span style={{ width: 22, textAlign: "center", display: "inline-flex", justifyContent: "center" }}>
          <Star size={14} strokeWidth={1.7} />
        </span>
        <span style={{ flex: 1 }}>Build your watchlist</span>
      </Link>
    );
  }
  return (
    <button
      onClick={onSignUp}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        width: "100%",
        padding: "10px 14px",
        background: "transparent",
        border: "none",
        color: "rgba(159,232,112,0.85)",
        fontSize: 13,
        textAlign: "left",
        cursor: "pointer",
        fontFamily: FONT_BASTION,
        transition: "background 0.1s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(159,232,112,0.06)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      <span style={{ width: 22, textAlign: "center", display: "inline-flex", justifyContent: "center" }}>
        <Star size={14} strokeWidth={1.7} />
      </span>
      <span style={{ flex: 1 }}>Sign up to add a watchlist</span>
    </button>
  );
}

/* ─── Animated loading state for picker trigger ─── */

function PickerTabsRow({
  tabs,
  activeCategory,
  onSelect,
}: {
  tabs: { key: PickerCategory; label: string; icon?: React.ReactNode }[];
  activeCategory: PickerCategory;
  onSelect: (key: PickerCategory) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const dragState = useRef({ dragging: false, startX: 0, startScroll: 0, moved: false });

  const check = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 2);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    check();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", check, { passive: true });
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => { el.removeEventListener("scroll", check); ro.disconnect(); };
  }, [check]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ds = dragState.current;

    const onDown = (e: PointerEvent) => {
      ds.dragging = true;
      ds.startX = e.clientX;
      ds.startScroll = el.scrollLeft;
      ds.moved = false;
      el.style.cursor = "grabbing";
      el.style.userSelect = "none";
    };
    const onMove = (e: PointerEvent) => {
      if (!ds.dragging) return;
      const dx = e.clientX - ds.startX;
      if (Math.abs(dx) > 3) ds.moved = true;
      el.scrollLeft = ds.startScroll - dx;
    };
    const onUp = () => {
      ds.dragging = false;
      el.style.cursor = "grab";
      el.style.userSelect = "";
    };

    el.addEventListener("pointerdown", onDown);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      el.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);

  const scroll = (dir: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    const step = el.clientWidth * 0.6;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const mask =
    canLeft && canRight ? "linear-gradient(to right, transparent 0%, black 20px, black calc(100% - 20px), transparent 100%)"
    : canRight ? "linear-gradient(to right, black calc(100% - 20px), transparent 100%)"
    : canLeft ? "linear-gradient(to right, transparent 0%, black 20px)"
    : "none";

  return (
    <div style={{ position: "relative", marginTop: 12 }}>
      {canLeft && (
        <button
          onClick={() => scroll(-1)}
          style={{
            position: "absolute", left: -2, top: "50%", transform: "translateY(-50%)",
            width: 18, height: 18, borderRadius: "50%", border: "1px solid #555",
            background: "#2a2a2a", color: "rgba(255,255,255,0.7)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", padding: 0, zIndex: 2, transition: "all 0.15s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#444"; e.currentTarget.style.color = "#fff"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "#2a2a2a"; e.currentTarget.style.color = "rgba(255,255,255,0.7)"; }}
        >
          <ChevronRight size={11} style={{ transform: "rotate(180deg)" }} />
        </button>
      )}
      {canRight && (
        <button
          onClick={() => scroll(1)}
          style={{
            position: "absolute", right: -2, top: "50%", transform: "translateY(-50%)",
            width: 18, height: 18, borderRadius: "50%", border: "1px solid #555",
            background: "#2a2a2a", color: "rgba(255,255,255,0.7)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", padding: 0, zIndex: 2, transition: "all 0.15s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#444"; e.currentTarget.style.color = "#fff"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "#2a2a2a"; e.currentTarget.style.color = "rgba(255,255,255,0.7)"; }}
        >
          <ChevronRight size={11} />
        </button>
      )}
      <div
        ref={scrollRef}
        className="picker-tabs-row"
        style={{
          display: "flex",
          gap: 4,
          overflowX: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch",
          maskImage: mask,
          WebkitMaskImage: mask,
          cursor: "grab",
          touchAction: "pan-x",
        }}
      >
        <style>{`
          .picker-tabs-row::-webkit-scrollbar { display: none; }
        `}</style>
        {tabs.map((tab) => {
          const active = activeCategory === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={(e) => {
                if (dragState.current.moved) { e.preventDefault(); return; }
                e.stopPropagation();
                onSelect(tab.key);
              }}
              aria-label={tab.key === "quick_picks" ? "Favorites" : tab.label || undefined}
              title={tab.key === "quick_picks" ? "Favorites" : undefined}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: tab.icon ? 4 : 0,
                padding: tab.key === "quick_picks" ? "6px 10px" : "6px 12px",
                fontSize: 12,
                fontWeight: 500,
                fontFamily: FONT_BASTION,
                color: active ? "#fff" : "rgba(255,255,255,0.5)",
                backgroundColor: active ? "#333" : "transparent",
                border: active ? "1px solid #555" : "1px solid transparent",
                borderRadius: 8,
                cursor: "pointer",
                transition: "all 0.15s ease",
                outline: "none",
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                if (!active) {
                  e.currentTarget.style.backgroundColor = "#2a2a2a";
                  e.currentTarget.style.borderColor = "#444";
                  e.currentTarget.style.color = "rgba(255,255,255,0.8)";
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.borderColor = "transparent";
                  e.currentTarget.style.color = "rgba(255,255,255,0.5)";
                }
              }}
            >
              {tab.key === "quick_picks" ? (
                <Star
                  size={13}
                  strokeWidth={1.8}
                  fill="none"
                  style={{ color: active ? BASTION_GOLD : "rgba(255,255,255,0.5)" }}
                />
              ) : (
                <>{tab.icon}{tab.label}</>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PickerFixedWidthLabel({
  label,
  labelStyle,
  labelMaxWidth,
}: {
  label: string;
  labelStyle: CSSProperties;
  labelMaxWidth: number;
}) {
  const textRef = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);

  const checkTruncated = useCallback(() => {
    const el = textRef.current;
    if (!el) return;
    setTruncated(el.scrollWidth > el.clientWidth + 1);
  }, []);

  useEffect(() => {
    checkTruncated();
    window.addEventListener("resize", checkTruncated);
    return () => window.removeEventListener("resize", checkTruncated);
  }, [label, labelMaxWidth, checkTruncated]);

  return (
    <BastionTooltip text={label} variant="metric" disabled={!truncated}>
      <span
        ref={textRef}
        className="hub-picker-trigger-label"
        style={{
          ...labelStyle,
          display: "block",
          maxWidth: labelMaxWidth,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textAlign: "left",
        }}
      >
        {label}
      </span>
    </BastionTooltip>
  );
}

function PickerEmptyState({
  label,
  size,
  weight,
}: {
  label: string;
  size: number;
  weight: number;
}) {
  return (
    <span
      style={{
        fontSize: size,
        fontWeight: weight,
        color: "rgba(255,255,255,0.38)",
        fontFamily: FONT_BASTION,
        letterSpacing: "-0.01em",
        lineHeight: 1,
      }}
    >
      {label}
    </span>
  );
}

function PickerLoadingState() {
  const [dots, setDots] = useState("");

  useEffect(() => {
    let i = 0;
    const id = setInterval(() => {
      i = (i + 1) % 4;
      setDots(".".repeat(i));
    }, 400);
    return () => clearInterval(id);
  }, []);

  return (
    <span
      style={{
        fontSize: 18,
        fontWeight: 500,
        color: "rgba(255,255,255,0.45)",
        fontFamily: FONT_BASTION,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        letterSpacing: "-0.01em",
      }}
    >
      <span
        style={{
          display: "inline-block",
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.35)",
          animation: "pickerPulse 1.2s ease-in-out infinite",
          flexShrink: 0,
        }}
      />
      <style>{`
        @keyframes pickerPulse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>
      <span style={{ minWidth: 110 }}>
        Scanning{dots}
      </span>
    </span>
  );
}
